import React, { useState, useEffect, useCallback } from 'react';
import Paper from '@mui/material/Paper';
import apiClient from '../api/client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

export default function StockFileStats() {
    const theme = useTheme();
    const [vendors, setVendors] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const chartSetting = {
        yAxis: [
          {
            label: 'SKU Count',
          },
        ],
        height: 380,
        sx: {
            [`.${axisClasses.left} .${axisClasses.label}`]: {
              transform: 'translate(-10px, 0)',
            },
        },
    };

    const getStockStats = useCallback(async () => {
        try {
            const res = await apiClient.get('/vendorstockstats');
            createDataSets(res.data || []);
        } catch (error) {
            console.error('Error fetching stock stats:', error);
            setHasError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        getStockStats();
    }, [getStockStats]);

    const createDataSets = (r) => {
        // aggregate per vendor so in-stock / out-of-stock bars stay aligned
        const byVendor = new Map();

        r.forEach((row) => {
            if (!byVendor.has(row.source)) {
                byVendor.set(row.source, { ins: 0, oos: 0 });
            }
            const counts = byVendor.get(row.source);
            if (row.stock_status === "oos") {
                counts.oos += row.cnt;
            } else {
                counts.ins += row.cnt;
            }
        });

        const vendorList = [...byVendor.keys()];
        setDatasets([
            { data: vendorList.map((v) => byVendor.get(v).ins), label: "In Stock" },
            { data: vendorList.map((v) => byVendor.get(v).oos), label: "Out of Stock" }
        ]);
        setVendors(vendorList);
    };

    return (
        <Paper sx={{ p: 2.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Vendor Stock Distribution
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Real-time Availability
                </Typography>
            </Box>

            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                {isLoading ? (
                    <Skeleton variant="rounded" width="100%" height={340} />
                ) : hasError || vendors.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                        {hasError ? 'Unable to load stock statistics' : 'No stock statistics available'}
                    </Typography>
                ) : (
                    <BarChart
                        xAxis={[{ scaleType: 'band', data: vendors }]}
                        series={datasets.map((d, i) => ({
                            ...d,
                            color: i === 0 ? theme.palette.success.main : theme.palette.error.main,
                        }))}
                        {...chartSetting}
                    />
                )}
            </Box>
        </Paper>
    );
}